import type { BoggleResolvedChallengeBonus } from '~/boggle/config/challenges/modTypes'
import { pickWeightedEntry } from '~/boggle/lib/challenge/random'
import { BOGGLE_ANCHOR_LETTER_ALLOWED_VALUES } from '~/boggle/config/challengeModLetterConstants'
import { buildBoggleResolvedChallengeGameplayFromModifiers } from '~/boggle/lib/challenge/resolvedModOps/resolveGameplay'
import { createPositiveLetterWeightVariants } from '~/boggle/lib/challenge/parameterVariants'
import { formatResolvedBonusDisplayFromDefinition } from '~/boggle/config/bonuses/formatResolvedBonusDisplay'
import type { BoggleChallengeBonusResolverContext } from '~/boggle/lib/challenge/resolveModFamily'
import type { BoggleBonusValue } from '../types'
import type { BoggleChallengeBonusDefinition } from '../bonusDefinition'

export const anchorLetterBonusDefinition: BoggleChallengeBonusDefinition = {
  description: 'Word includes the seeded letter',
  formatResolvedDisplay: ({ parameters }) => {
    const letter = String(parameters.letter?.value ?? '')
    if (letter.length === 0) {
      return {
        description: 'Word includes the seeded letter',
        name: 'Anchor',
        shortLabel: 'Use a letter',
      }
    }

    return {
      description: `Word includes the letter ${letter}`,
      name: `Anchor (${letter})`,
      shortLabel: `Use ${letter}`,
    }
  },
  id: 'anchor-letter',
  name: 'Anchor Letter',
  parameterDefinitions: {
    letter: {
      kind: 'enum',
      max: BOGGLE_ANCHOR_LETTER_ALLOWED_VALUES.length - 1,
      min: 0,
      values: BOGGLE_ANCHOR_LETTER_ALLOWED_VALUES,
    },
  },
  selectionWeight: 1,
  shortLabel: 'Use a letter',
}

export function resolveAnchorLetterBonus(
  family: BoggleChallengeBonusDefinition,
  rngState: number,
  context: BoggleChallengeBonusResolverContext,
  value: BoggleBonusValue,
): {
  mod: BoggleResolvedChallengeBonus
  rngState: number
} | null {
  const allowedLetters = new Set(BOGGLE_ANCHOR_LETTER_ALLOWED_VALUES)
  const letterVariants = createPositiveLetterWeightVariants(
    buildBoggleResolvedChallengeGameplayFromModifiers(context.selectedMods).letterWeights,
  ).filter(variant => allowedLetters.has(variant.letter))

  if (letterVariants.length === 0) {
    return null
  }

  const pickedLetter = pickWeightedEntry(letterVariants, rngState)
  if (pickedLetter === null) {
    return null
  }

  const parameters = {
    letter: {
      definition: family.parameterDefinitions.letter!,
      value: pickedLetter.entry.letter,
    },
  }
  const display = formatResolvedBonusDisplayFromDefinition(family, parameters)

  return {
    mod: {
      description: display.description,
      effects: [],
      familyId: family.id,
      id: `${family.id}-${pickedLetter.entry.letter.toLowerCase()}`,
      name: display.name,
      outcomes: [
        {
          kind: 'required-letter',
          letter: pickedLetter.entry.letter,
        },
      ],
      parameters,
      shortLabel: display.shortLabel,
      value,
    },
    rngState: pickedLetter.rngState,
  }
}
